import { createSlice, createSelector } from '@reduxjs/toolkit';
import { LearningModuleState, LearningModuleStateData } from 'src/types/state/learningModule';
import { ContentProgressStatus } from 'src/types/contentProgress';
import { IconProgressStatus } from '@amzn/seller-university-ui-components';
import { ContentProgressEventCode, ContentProgressPOSTResponse } from 'src/types/api/contentProgress';
import { toContentProgressStatus } from 'src/utils/contentProgress';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { suApiSlice } from './api/su';
import { LearningPathwayStaticApiData } from '../../types/api/learningPathwayStatic';
import type { AppState } from '../store';

// State of the Learning Modules Slice
export interface LearningModulesSliceState {
  byId: Record<string, LearningModuleState>;
  // Store for additional learning module metadata
}

export const initialState: LearningModulesSliceState = {
  byId: {},
};

export const learningModulesSlice = createSlice({
  name: 'learningModules',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Helper to process learning pathway data into modules store
    const processLearningPathwayModules = (
      state: LearningModulesSliceState,
      learningPathwayData: LearningPathwayStaticApiData,
    ) => {
      if (!learningPathwayData?.sections) {
        ClientLogger.error(ClientLog.PayloadProcessingError, {
          payload: learningPathwayData,
          url: window.location.href,
        });
        return;
      }

      learningPathwayData.sections.forEach((section) => {
        section.moduleList.forEach((learningModule) => {
          state.byId[learningModule.moduleId] = { data: learningModule };
        });
      });
    };

    builder
      .addMatcher(
        suApiSlice.endpoints.getLearningPathway.matchFulfilled,
        (state, action) => {
          try {
            // Metadata-only responses intentionally omit sections — skip module processing
            if (action.meta.arg.originalArgs?.metadataOnly) {
              return;
            }
            processLearningPathwayModules(state, action.payload.learningPathway);
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getLearningPathwayStatic.matchFulfilled,
        (state, action) => {
          try {
            processLearningPathwayModules(state, action.payload.learningPathway);
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningModules.matchFulfilled,
        (state, action) => {
          try {
            const learningModulesById: Record<string, LearningModuleState> = {};

            action.payload.modules.forEach((moduleData) => {
              if (!moduleData?.moduleId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: moduleData,
                  url: window.location.href,
                });
                return;
              }

              learningModulesById[moduleData.moduleId] = {
                data: moduleData,
              };
            });

            state.byId = {
              ...state.byId,
              ...learningModulesById,
            };
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              payload: action.payload,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listModuleCards.matchFulfilled,
        (state, action) => {
          try {
            const learningModulesById: Record<string, LearningModuleState> = {};

            action.payload.cards.forEach((moduleCardData) => {
              if (!moduleCardData?.moduleId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: moduleCardData,
                  url: window.location.href,
                });
                return;
              }

              learningModulesById[moduleCardData.moduleId] = {
                data: {
                  ...moduleCardData,
                  instanceMap: moduleCardData.instanceMap || {},
                  moduleProgressStatus:
                    state.byId[moduleCardData.moduleId]?.data?.moduleProgressStatus
                    ?? ContentProgressStatus.NOT_STARTED,
                },
              };
            });

            state.byId = {
              ...state.byId,
              ...learningModulesById,
            };
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              payload: action.payload,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getLearningModule.matchFulfilled,
        (state, action) => {
          try {
            const moduleData = action.payload.module;

            if (!moduleData?.moduleId) {
              ClientLogger.error(ClientLog.IllegalInternalDataState, {
                payload: moduleData,
                url: window.location.href,
              });
              return;
            }

            // Store under the canonical moduleId from the response
            state.byId[moduleData.moduleId] = {
              data: moduleData,
            };

            // Also store under the request moduleId if it differs from the response moduleId.
            // This handles legacy moduleId mapping: NCPS maps legacy IDs to current IDs,
            // so the response moduleId may differ from the requested one.
            // Without this alias, the store lookup by the legacy ID would miss.
            const requestModuleId = action.meta.arg.originalArgs?.moduleId;
            if (requestModuleId && requestModuleId !== moduleData.moduleId) {
              state.byId[requestModuleId] = {
                data: moduleData,
              };

              ClientLogger.warn(ClientLog.LegacyIdAccess, {
                legacyModuleId: requestModuleId,
                resolvedModuleId: moduleData.moduleId,
                moduleTitle: moduleData.title,
                url: window.location.href,
              });
            }
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              payload: action.payload,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.addContentProgress.matchFulfilled,
        (state, action) => {
          // Process content progress data into our store from the AddContentProgress API
          const contentProgressPayload: ContentProgressPOSTResponse = action.payload;
          const learningModuleId = action.meta.arg.originalArgs.instance.moduleId;
          const learningModule = state.byId[learningModuleId];
          if (learningModule) {
            const progressEventCode: ContentProgressEventCode = contentProgressPayload.contentProgressStatus;
            const updatedModuleProgressStatus: ContentProgressStatus = toContentProgressStatus(progressEventCode);
            learningModule.data.moduleProgressStatus = updatedModuleProgressStatus;
          } else {
            ClientLogger.error(
              ClientLog.PayloadProcessingError,
              {
                moduleId: learningModuleId,
                url: window.location.href,
              },
            );
          }
        },
      );
    // Add more matchers that may react to another slice's actions/APIs, e.g. updating an individual module's progress status
  },
});

// Basic selectors
export const learningModuleActions = learningModulesSlice.actions;
export const selectLearningModuleById = (
  state: AppState,
  learningModuleId: string,
) => {
  return state.learningModules.byId[learningModuleId];
};

export const selectLearningModuleExists = (
  state: AppState,
  learningModuleId: string | null,
) => {
  return learningModuleId && Boolean(state.learningModules.byId[learningModuleId]);
};

export const selectModuleDataByIds = createSelector(
  [
    (state: AppState) => state.learningModules.byId,
    (state: AppState, moduleIds: string[]) => moduleIds,
  ],
  (modulesById, moduleIds) => {
    const result: Record<string, LearningModuleStateData> = {};
    moduleIds.forEach((moduleId) => {
      const module = modulesById[moduleId];
      if (module && module.data) {
        result[moduleId] = module.data;
      }
    });
    return result;
  },
);

/**
 * Selects the appropriate progress icon for a section based on its modules' statuses
 * @param state The Redux state
 * @param moduleIds Array of module IDs in the section
 * @returns The appropriate IconProgressStatus ('complete', 'inProgress', or 'hidden')
 */
export const determineSectionProgressIcon = createSelector(
  [
    (state: AppState) => state.learningModules.byId,
    (state: AppState, moduleIds: string[]) => moduleIds,
  ],
  (modulesById, moduleIds): IconProgressStatus => {
    if (!moduleIds || moduleIds.length === 0) return 'hidden';

    let hasCompleted = false;
    let hasInProgress = false;
    let hasNotStarted = false;

    moduleIds.forEach((id) => {
      const module = modulesById[id];
      if (!module?.data) return;
      const status = module.data.moduleProgressStatus;
      if (status === ContentProgressStatus.COMPLETED) {
        hasCompleted = true;
      } else if (status === ContentProgressStatus.IN_PROGRESS) {
        hasInProgress = true;
      } else {
        hasNotStarted = true;
      }
    });

    if (hasInProgress || (hasCompleted && hasNotStarted)) {
      return 'inProgress';
    } if (hasCompleted) {
      return 'complete';
    }
    return 'hidden';
  },
);
